import { useMemo, useState } from 'react';
import useSnackbar from 'shared/hooks/useSnackbar';
import { TicketingIntegration } from 'shared/types';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import {
  INCIDENT_TICKETS,
  INCIDENT_GRAPHQL_DETAIL,
  INCIDENT_LIST_PAGED,
  INCIDENT_LIST_GROUP_SUMMARY,
} from '@elementary/common-ui/constants/queryKeys';
import useSelectedEnv from '@elementary/common-ui/hooks/useSelectedEnv';
import { createIncidentTicket } from 'incidents/api';

type Props = {
  integrations?: TicketingIntegration[];
  incidentUrl: string;
  incidentId: string;
};

const useCreateTicket = ({ integrations, incidentUrl, incidentId }: Props) => {
  const { openSnackbar } = useSnackbar();
  const queryClient = useQueryClient();
  const selectedEnvId = useSelectedEnv();

  const [ticketDialogOpen, setTicketDialogOpen] = useState(false);
  const [selectedIntegrationId, setSelectedIntegrationId] = useState<
    string | null
  >(null);
  const [dialogError, setDialogError] = useState<Error | null>(null);

  const { mutateAsync: createTicketMutation, isLoading: isCreatingTicket } =
    useMutation(
      async ({
        integrationId,
        customFields,
      }: {
        integrationId: string;
        customFields?: Record<string, unknown>;
      }) => {
        const integration = integrations?.find(
          (integration) => integration.id === integrationId,
        );

        if (!integration) {
          throw new Error('Integration not found');
        }
        if (!selectedEnvId) {
          throw new Error('Environment not found');
        }

        try {
          const ticket = await createIncidentTicket(
            selectedEnvId,
            incidentId,
            incidentUrl,
            integrationId,
            customFields,
          );

          queryClient.invalidateQueries([
            INCIDENT_TICKETS,
            selectedEnvId,
            incidentId,
          ]);
          queryClient.invalidateQueries([
            INCIDENT_GRAPHQL_DETAIL,
            incidentId,
            selectedEnvId,
          ]);
          queryClient.invalidateQueries([INCIDENT_LIST_PAGED]);
          queryClient.invalidateQueries([INCIDENT_LIST_GROUP_SUMMARY]);

          openSnackbar({
            variant: 'success',
            message: `Created ${integration.type} ticket`,
            actions: [
              {
                text: 'View ticket',
                cb: () => window.open(ticket.service_ticket_url, '_blank'),
              },
            ],
          });

          setTicketDialogOpen(false);
          setSelectedIntegrationId(null);
        } catch (error) {
          setDialogError(
            error instanceof Error
              ? error
              : new Error('Failed to create ticket for incident'),
          );

          openSnackbar({
            variant: 'error',
            message: `Failed to create ${integration.type} ticket for incident`,
          });
        }
      },
    );

  const selectedIntegration = useMemo(
    () =>
      integrations?.find(
        (integration) => integration.id === selectedIntegrationId,
      ),
    [integrations, selectedIntegrationId],
  );

  const onCreateTicketClick = (integrationId: string) => {
    const integration = integrations?.find(
      (integration) => integration.id === integrationId,
    );

    if (integration?.type === 'jira' || integration?.type === 'linear') {
      setSelectedIntegrationId(integrationId);
      setTicketDialogOpen(true);
    } else {
      createTicketMutation({ integrationId });
    }
  };

  const handleDialogConfirm = async (customFields?: Record<string, any>) => {
    if (!selectedIntegrationId) return;

    setDialogError(null);

    await createTicketMutation({
      integrationId: selectedIntegrationId,
      customFields,
    });
  };

  const handleDialogClose = () => {
    setTicketDialogOpen(false);
    setSelectedIntegrationId(null);
    setDialogError(null);
  };

  return {
    ticketDialogOpen,
    selectedIntegration,
    onCreateTicketClick,
    handleDialogConfirm,
    handleDialogClose,
    isCreatingTicket,
    dialogError,
  };
};

export default useCreateTicket;
